import React, { useEffect, useState } from "react";
import { IndianRupee, ShoppingCart, Package, Users, TrendingUp, AlertTriangle } from "lucide-react";
import { apiGetStats, apiGetLowStockProducts } from "../../api";

const StatCard = ({ icon: Icon, label, value, accent }) => (
  <div className="bg-white rounded-sm border border-neutral-200 p-5">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-xs uppercase tracking-widest text-neutral-500">{label}</p>
        <p className="text-2xl font-bold text-neutral-900 mt-2">{value}</p>
      </div>
      <div className={`h-11 w-11 rounded-sm flex items-center justify-center ${accent}`}>
        <Icon className="h-5 w-5" />
      </div>
    </div>
  </div>
);

const statusColor = (s) => ({
  Pending: "bg-amber-100 text-amber-700",
  Confirmed: "bg-blue-100 text-blue-700",
  Shipped: "bg-purple-100 text-purple-700",
  Delivered: "bg-green-100 text-green-700",
  Cancelled: "bg-red-100 text-red-700",
}[s] || "bg-neutral-100 text-neutral-700");

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [lowStock, setLowStock] = useState([]);

  useEffect(() => {
    apiGetStats().then(setStats).catch(() => setStats(null));
    apiGetLowStockProducts().then(setLowStock).catch(() => setLowStock([]));
  }, []);

  if (!stats) return <p className="text-neutral-500">Loading dashboard...</p>;

  return (
    <div>
      <h1 className="font-serif text-3xl text-neutral-900 mb-6">Dashboard</h1>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard icon={IndianRupee} label="Revenue" value={`₹${stats.revenue.toLocaleString("en-IN")}`} accent="bg-[#faf0e0] text-[#c9954a]" />
        <StatCard icon={ShoppingCart} label="Orders" value={stats.ordersCount} accent="bg-[#f3e8ea] text-[#e8607a]" />
        <StatCard icon={Package} label="Products" value={stats.productsCount} accent="bg-blue-50 text-blue-600" />
        <StatCard icon={Users} label="Customers" value={stats.customersCount} accent="bg-green-50 text-green-600" />
      </div>

      {/* Low Stock Alert */}
      {lowStock.length > 0 && (
        <div className="mb-6 bg-amber-50 border border-amber-200 rounded-sm p-5">
          <h2 className="font-semibold text-amber-800 mb-3 flex items-center gap-2">
            <AlertTriangle className="h-4 w-4" />
            Low Stock Alert ({lowStock.length} products)
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {lowStock.map((p) => (
              <div key={p.id} className="flex items-center justify-between bg-white border border-amber-100 rounded-sm px-3 py-2.5">
                <div>
                  <p className="text-sm font-medium text-neutral-800 line-clamp-1">{p.name}</p>
                  <p className="text-xs text-neutral-500 mt-0.5">{p.category}</p>
                </div>
                <span className={`text-xs font-bold px-2 py-0.5 rounded-full ml-2 whitespace-nowrap ${p.stock === 0 ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}>
                  {p.stock === 0 ? "Out" : `${p.stock} left`}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white rounded-sm border border-neutral-200 p-6">
          <h2 className="font-semibold text-neutral-900 mb-4">Recent Orders</h2>
          {stats.recentOrders.length === 0 ? (
            <p className="text-sm text-neutral-500">No orders yet.</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-neutral-500 border-b border-neutral-200">
                  <th className="py-2 font-medium">Order</th>
                  <th className="py-2 font-medium">Customer</th>
                  <th className="py-2 font-medium">Total</th>
                  <th className="py-2 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {stats.recentOrders.map((o) => (
                  <tr key={o.id} className="border-b border-neutral-100">
                    <td className="py-2.5 font-medium text-neutral-800">{o.orderNumber}</td>
                    <td className="py-2.5 text-neutral-600">{o.customer?.name}</td>
                    <td className="py-2.5">₹{o.total.toLocaleString("en-IN")}</td>
                    <td className="py-2.5">
                      <span className={`px-2 py-0.5 rounded-full text-xs ${statusColor(o.status)}`}>{o.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="bg-white rounded-sm border border-neutral-200 p-6">
          <h2 className="font-semibold text-neutral-900 mb-4 flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-[#c9954a]" /> Top Products
          </h2>
          {stats.topProducts.length === 0 ? (
            <p className="text-sm text-neutral-500">No sales yet.</p>
          ) : (
            <ul className="space-y-3">
              {stats.topProducts.map((p, i) => (
                <li key={p.name} className="flex items-center justify-between text-sm">
                  <span className="text-neutral-700">
                    <span className="text-[#c9954a] font-semibold mr-2">{i + 1}.</span>{p.name}
                  </span>
                  <span className="font-medium text-neutral-900">{p.sold} sold</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
